import { BehaviorSubject } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { DiskService } from '../disk/disk.service';
import { PREFIX_SEARCH_ENDKEY_UNICODE } from '@omni/config/pouch-db.config';
import { DeltaService, EntityNames } from '@omni/data-services/delta/delta.service';
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';

/**
 * Base class for offline data services that provides standardized synchronization
 * and local storage management for CRM entities.
 * 
 * This abstract class handles:
 * - Data synchronization from CRM to local storage
 * - Incremental sync based on last updated timestamps
 * - Local data retrieval and caching
 * - Sync state management and error tracking
 * 
 * @template TRaw - The raw data type from CRM
 * @template Key - The key attribute name for the entity ID
 */

export abstract class OfflineServiceBase<TRaw, Key extends keyof TRaw = keyof TRaw> {
  /** BehaviorSubject to trigger data refresh when sync is completed */
  readonly #syncCompleted = new BehaviorSubject<void>(null);

  /** Observable that provides raw data from local disk storage with caching */
  protected readonly raw$ = this.#syncCompleted
    .pipe(
      switchMap(() => this.#readFromDisk()),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

  /** Emits whenever raw$ would re-read the disk, without reading it: for a consumer after one record. */
  protected readonly dataChanged$ = this.#syncCompleted.asObservable();

  /**
   * Initializes the OfflineServiceBase with required dependencies.
   * @param disk - Disk service for local storage operations
   * @param deltaService - Delta service for sync state management
   * @param entityName - Name of the CRM entity being managed
   * @param idAttribute - Key attribute name for the entity ID
   * @param dbKeyPrefix - Database key prefix for local storage
   */
  constructor(
    protected readonly disk: DiskService,
    protected readonly deltaService: DeltaService,
    private readonly entityName: EntityNames,
    protected readonly idAttribute: Key,
    protected readonly dbKeyPrefix: string,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
  ) { }

  /**
   * Re-emits the raw$ stream so consumers pick up local writes that happened
   * outside the sync cycle (e.g. a record created on-device by a writable
   * subclass). Local storage is the source of truth; emitting is enough.
   */
  protected notifyLocalChange() {
    this.#syncCompleted.next();
  }

  /**
   * Renames top-level `_xxx` properties to `dyn__xxx` before PouchDB write.
   * PouchDB rejects top-level keys starting with `_` except `_id`, `_rev`, `_deleted`.
   */
  static sanitizeForPouchDB<T extends Record<string, any>>(doc: T): T {
    const sanitized = {} as Record<string, any>;
    const reserved = new Set(['_id', '_rev', '_deleted']);
    for (const key of Object.keys(doc)) {
      if (key.startsWith('_') && !reserved.has(key)) {
        sanitized[`dyn_${key}`] = doc[key];
      } else {
        sanitized[key] = doc[key];
      }
    }
    return sanitized as T;
  }

  /**
   * Restores `dyn__xxx` properties back to `_xxx` after PouchDB read.
   */
  static unsanitizeFromPouchDB<T extends Record<string, any>>(doc: T): T {
    const restored = {} as Record<string, any>;
    for (const key of Object.keys(doc)) {
      if (key.startsWith('dyn__')) {
        restored[key.substring(4)] = doc[key];
      } else {
        restored[key] = doc[key];
      }
    }
    return restored as T;
  }

  /**
   * Reads raw data from local disk storage using PouchDB query.
   * Filters for records with the entity's database key prefix. Every doc goes
   * back through unsanitizeFromPouchDB so consumers of raw$ see the entity's own
   * column names (`_indskr_target_value`), not the `dyn__` spelling disk needs.
   * @returns Promise<TRaw[]> Array of raw data records
   */
  async #readFromDisk() {
    const option = {
      selector: {
        '_id': {
          $gte: this.dbKeyPrefix + "_",
          $lte: this.dbKeyPrefix + "_" + PREFIX_SEARCH_ENDKEY_UNICODE,
        },
      }
    };
    try {
      const results = await this.disk.find<TRaw>(option);
      return (results ?? []).map(doc => OfflineServiceBase.unsanitizeFromPouchDB(doc));
    } catch (e) {
      // Never let a transient disk error (e.g. DB not ready at early init) kill
      // the raw$ stream — an errored observable terminates and would never
      // re-emit on a later #syncCompleted. Return empty and recover next sync.
      console.warn('OfflineServiceBase readFromDisk failed; recovering on next sync:', e);
      return [];
    }
  }

  /**
   * Abstract method that must be implemented by derived classes to fetch data from CRM.
   * @param lastUpdatedTime - ISO string timestamp for incremental sync filtering
   * @returns Promise of processed data array that can include deleted records
   */
  protected abstract fetchData(lastUpdatedTime: string): Promise<(Omit<TRaw, '_deleted'> | (Pick<TRaw, Key> & { _deleted: boolean }))[]>;

  /**
   * Synchronizes data from CRM to local storage.
   * Handles incremental sync based on last updated time and manages sync state.
   * @param fullSync - Whether to perform a full sync (ignoring last updated time)
   * @param loadFromDBOnly - If true, skips sync and only loads from local DB
   */
  public async sync(fullSync?: boolean, loadFromDBOnly = false) {
    if (loadFromDBOnly) {
      // Re-emit so consumers load the current local data even when no network
      // sync runs — recovers in-memory caches whose first read happened before
      // the local DB was ready.
      this.#syncCompleted.next();
      return;
    }
    if (!(await Promise.resolve(this.isSyncAllowed()))) {
      return;
    }

    // Get or create sync state to track last sync time
    let syncState = await this.disk.getSyncState(
      this.dbKeyPrefix
    );
    if (!syncState) {
      await this.disk.createDocument(this.dbKeyPrefix, {
        lastUpdatedTime: null,
      });
      syncState = await this.disk.getSyncState(this.dbKeyPrefix);
    }

    // Determine last updated time for incremental sync
    let lastUpdatedTime = null;
    if (!fullSync && syncState?.lastUpdatedTime) {
      lastUpdatedTime = new Date(parseInt(syncState.lastUpdatedTime)).toISOString();
    }
    const currentTime = new Date().getTime();
    
    // Initialize sync info for tracking sync progress and errors
    const syncInfo = {
      entityName: this.entityName,
      totalFailed: 0,
      totalSynced: 0,
      errors: [],
      syncStatus: true
    };

    let fetchedData: (Omit<TRaw, '_deleted'> | (Pick<TRaw, Key> & { _deleted: boolean }))[];

    try {
      // Fetch data from CRM using the implemented fetchData method
      fetchedData = await this.fetchData(lastUpdatedTime);
      // Update timestamp for next sync
      lastUpdatedTime = currentTime;
    } catch (error) {
      console.error('Error fetching data: ', error);
      this.deltaService.addSyncErrorToEntitySyncInfo(syncInfo, '', error);
      this.deltaService.addEntitySyncInfo(syncInfo);
      return;
    }

    const written = await this.#writePulledRows(fetchedData);

    // No new data — still re-emit so consumers reflect the current local DB
    // (recovers caches whose initial read ran before the DB was ready).
    if (!written.attempted) {
      this.#syncCompleted.next();
      return;
    }

    // A falsy result means DiskService.bulk caught a throw (doc validation, a
    // closed DB, out of memory) and returned nothing: not one row landed. Leave
    // lastUpdatedTime where it is so the next delta asks for these rows again.
    if (!written.landed) {
      const error = new Error(`bulk write returned no result for ${written.attempted} rows`);
      console.error(`OfflineServiceBase(${this.entityName}): ${error.message}; the delta watermark stays put`);
      syncInfo.syncStatus = false;
      syncInfo.totalFailed = written.attempted;
      this.deltaService.addSyncErrorToEntitySyncInfo(syncInfo, '', error);
      this.deltaService.addEntitySyncInfo(syncInfo);
      this.#syncCompleted.next();
      return;
    }

    this.#syncCompleted.next();

    // Update sync info and save sync state
    syncInfo.totalSynced = written.attempted;
    this.deltaService.addEntitySyncInfo(syncInfo);
    await this.disk.updateSyncState({
      ...syncState,
      lastUpdatedTime: lastUpdatedTime,
    });
  }

  /**
   * Writes rows pulled from Dynamics outside the delta sync, then refreshes what
   * consumers see. The sync time does not move, so the next delta still asks for
   * everything changed since the last full pass. Rows go through the same merge
   * as a sync: tombstones delete and {@link shouldPreserveLocalDoc} keeps local edits.
   *
   * @returns Whether the store accepted the rows.
   */
  protected async storePulledRows(rows: (Omit<TRaw, '_deleted'> | (Pick<TRaw, Key> & { _deleted: boolean }))[]): Promise<boolean> {
    const written = await this.#writePulledRows(rows);
    this.#syncCompleted.next();
    return written.landed;
  }

  /**
   * Merges pulled rows into the local store. Conflicted writes get one retry with
   * the stored revision.
   *
   * @returns How many rows were written, and whether the store accepted the write.
   */
  async #writePulledRows(fetchedData: (Omit<TRaw, '_deleted'> | (Pick<TRaw, Key> & { _deleted: boolean }))[]): Promise<{ attempted: number; landed: boolean }> {
    // Fetch all existing docs for this entity in a single range query instead of N individual reads
    const existingDocs = await this.disk.find<TRaw>({
      selector: {
        '_id': {
          $gte: this.dbKeyPrefix + "_",
          $lte: this.dbKeyPrefix + "_" + PREFIX_SEARCH_ENDKEY_UNICODE,
        },
      }
    }) ?? [];
    const existingDocMap = new Map(existingDocs.map(doc => [doc['_id'], doc]));

    // Process each data item and prepare for local storage
    const data = fetchedData.map(item => {
      const _id = `${this.dbKeyPrefix}_${(item as Pick<TRaw, Key>)[this.idAttribute]}`;
      const doc = existingDocMap.get(_id);

      // Handle deleted items - mark for deletion in local storage.
      // Tombstones are applied unconditionally: a record deleted server-side is
      // gone regardless of local state (delta delivers the tombstone only once).
      if (item['_deleted']) {
        if (!doc) {
          return null;
        }
        return {
          _id,
          _rev: doc['_rev'],
          _deleted: true,
        };
      }

      // Give subclasses a veto over merging a pulled row into this local doc.
      if (doc && this.shouldPreserveLocalDoc(doc)) {
        return null;
      }

      // Merge with existing document or create new one. PouchDB rejects any
      // top-level key starting with `_` other than its own, so a pulled row
      // carrying a Dynamics lookup value (`_xxx_value`) has to be renamed before
      // it can be written; #readFromDisk restores the original names on the way
      // back out.
      return OfflineServiceBase.sanitizeForPouchDB({
        ...doc ?? {},
        _id,
        ...item,
      });
    }).filter(Boolean);

    if (!data.length) {
      return { attempted: 0, landed: true };
    }

    // Bulk save to local storage and trigger data refresh. bulkDocs reports
    // per-doc failures in its RESULT (it does not throw for conflicts), so the
    // results must be checked — a conflicted doc would otherwise stay stale
    // forever while the sync reports success. Conflicts get one retry with the
    // freshly-stored _rev.
    type BulkResult = { id?: string; ok?: boolean; rev?: string; error?: boolean; name?: string };
    const bulkResults = (await this.disk.bulk(data)) as BulkResult[] | undefined;

    if (!bulkResults) {
      return { attempted: data.length, landed: false };
    }

    const results: BulkResult[] = bulkResults;
    const failed = results.filter(r => r?.error);
    if (failed.length) {
      console.error(`OfflineServiceBase(${this.entityName}): ${failed.length}/${data.length} bulk writes failed`, failed.slice(0, 3));
      const failedIds = new Set(failed.map(f => f.id));
      const retryDocs = [];
      for (const doc of data) {
        if (!doc || !failedIds.has(doc['_id'])) continue;
        const current = await this.disk.retrieve(doc['_id'], true);
        retryDocs.push({ ...doc, _rev: current?.['_rev'] });
      }
      if (retryDocs.length) {
        const retryResults = (await this.disk.bulk(retryDocs)) as BulkResult[] | undefined;
        if (!retryResults) {
          console.error(`OfflineServiceBase(${this.entityName}): retry of ${retryDocs.length} writes returned no result`);
        } else {
          const stillFailed = retryResults.filter(r => r?.error);
          if (stillFailed.length) {
            console.error(`OfflineServiceBase(${this.entityName}): ${stillFailed.length} writes still failing after retry`, stillFailed.slice(0, 3));
          }
        }
      }
    }
    return { attempted: data.length, landed: true };
  }

  protected isSyncAllowed(): boolean | Promise<boolean> {
    return true;
  }

  /**
   * Whether a pulled (non-tombstone) row must NOT overwrite this local doc.
   * The base never preserves: pull-only entities have no local writes to
   * protect. Writable subclasses override this to protect docs whose local
   * changes have not been pushed yet.
   */
  protected shouldPreserveLocalDoc(_doc: TRaw): boolean {
    return false;
  }
}

/**
 * Result of pushing one locally-created/edited record to CRM. `serverId` maps an
 * offline id to the real GUID assigned by the server; records that failed to push
 * are simply omitted from the result and stay pending for the next cycle.
 */
export interface PushOutcome {
  /** The id the record had locally when it was pushed (offline id or GUID). */
  localId: string;
  /** The server-assigned GUID; equals localId when the record already had one. */
  serverId: string;
}

/**
 * Extension of OfflineServiceBase for entities that are CREATED and EDITED on the
 * device, not just pulled from CRM. Adds a local write API and a push hook:
 *
 * - Local writes go straight to disk with `pendingPushToDynamics: true` and an
 *   offline id (`offline_<entity>_<rand>_<ts>`), then re-emit raw$ so consumers
 *   update reactively. When `offlineDataCountKey` is set, pending counts are
 *   maintained on every write so `DiskService.isThereOfflineDataToBeUploaded`
 *   sees the queue — set it only when an upload routine exists to drain it.
 * - sync() pushes BEFORE it pulls (matching the master-data-service ordering),
 *   both gated by isSyncAllowed(); the pull merge preserves docs that are still
 *   pending push (shouldPreserveLocalDoc), while server tombstones always apply.
 */
export abstract class OfflineWritableServiceBase<TRaw extends Record<string, any>, Key extends keyof TRaw = keyof TRaw> extends OfflineServiceBase<TRaw, Key> {
  /** Offline-id prefix, e.g. 'offline_voicenote'. */
  protected abstract readonly offlineIdPrefix: string;
  /**
   * Key in OFFLINE_DATA_COUNT_ENTITY_NAME for pending-push bookkeeping. Leave
   * undefined for a device-local entity: publishing a count wires the entity
   * into the global upload cycle (isThereOfflineDataToBeUploaded), which must
   * only see queues that an upload routine actually drains.
   */
  protected readonly offlineDataCountKey?: string;

  /**
   * Pushes pending records to CRM and reports which ones landed. Called by
   * sync() before the pull. Return [] when there is nothing to push. Must not
   * throw for per-record failures — omit failures instead.
   */
  protected abstract pushData(pending: TRaw[]): Promise<PushOutcome[]>;

  /** Creates a record locally; returns the stored raw doc (with generated id). */
  public async create(item: Omit<TRaw, Key> & Partial<Pick<TRaw, Key>>): Promise<TRaw> {
    const id = (item[this.idAttribute as string] as string) ?? this.#generateOfflineId();
    const doc = OfflineServiceBase.sanitizeForPouchDB({
      ...item,
      [this.idAttribute]: id,
      pendingPushToDynamics: true,
    } as unknown as TRaw);
    await this.disk.updateOrInsert(this.#docKey(id), () => doc);
    await this.#refreshPendingCount();
    this.notifyLocalChange();
    return doc;
  }

  /** Merges a patch into an existing local record and marks it pending push. */
  public async update(id: string, patch: Partial<TRaw>): Promise<TRaw> {
    const existing = await this.disk.retrieve(this.#docKey(id), true);
    if (!existing) {
      throw new Error(`${this.constructor.name}: cannot update missing record ${id}`);
    }
    const doc = OfflineServiceBase.sanitizeForPouchDB({
      ...existing,
      ...patch,
      [this.idAttribute]: id,
      pendingPushToDynamics: true,
    });
    await this.disk.updateOrInsert(this.#docKey(id), () => doc);
    await this.#refreshPendingCount();
    this.notifyLocalChange();
    return OfflineServiceBase.unsanitizeFromPouchDB(doc);
  }

  /**
   * Removes a record locally. Never-pushed records (offline id) are hard-deleted;
   * server-backed records are tombstoned (`isDeleted` + pending push) so the
   * deletion propagates on the next push cycle.
   */
  public async removeLocal(id: string): Promise<void> {
    if (id.startsWith(this.offlineIdPrefix)) {
      await this.disk.remove(this.#docKey(id));
    } else {
      const existing = await this.disk.retrieve(this.#docKey(id), true);
      if (!existing) {
        return;
      }
      await this.disk.updateOrInsert(this.#docKey(id), () => ({
        ...existing,
        isDeleted: true,
        pendingPushToDynamics: true,
      }));
    }
    await this.#refreshPendingCount();
    this.notifyLocalChange();
  }

  /** Convenience: load local data into raw$ without a network sync. */
  public async load() {
    await this.sync(false, true);
  }

  /**
   * Pushes pending records without pulling — the entry point for the global
   * offline upload cycle (MasterDataService.uploadOfflineData). sync() covers
   * the master-data path, which pulls as well; a push-only cycle never calls it,
   * so this is what actually drains the queue offlineDataCountKey publishes.
   *
   * Returns what settled, so a caller that has just written a record can find
   * the id Dynamics gave it: a settled record moves to a new key and the id the
   * caller is holding no longer resolves.
   */
  public async pushOfflineData(): Promise<PushOutcome[]> {
    if (!(await Promise.resolve(this.isSyncAllowed()))) {
      return [];
    }
    return this.#pushPending();
  }

  /** Push-before-pull, then the base pull cycle. Both gated by isSyncAllowed. */
  public override async sync(fullSync?: boolean, loadFromDBOnly = false) {
    if (!loadFromDBOnly && (await Promise.resolve(this.isSyncAllowed()))) {
      await this.#pushPending();
    }
    await super.sync(fullSync, loadFromDBOnly);
  }

  #pushQueue: Promise<unknown> = Promise.resolve();

  protected override shouldPreserveLocalDoc(doc: TRaw): boolean {
    return doc['pendingPushToDynamics'] === true;
  }

  /**
   * Runs one push at a time. A push started while another is in flight reads the
   * same pending records and creates each of them a second time; queued behind
   * the first, it finds them already settled.
   */
  #pushPending(): Promise<PushOutcome[]> {
    const run = this.#pushQueue.then(() => this.#pushPendingNow());
    this.#pushQueue = run.catch(() => undefined);
    return run;
  }

  async #pushPendingNow(): Promise<PushOutcome[]> {
    const pending = await this.#findPendingDocs();
    if (!pending.length) {
      return [];
    }
    const pushedById = new Map(pending.map(doc => [String(doc[this.idAttribute as string]), doc]));
    let outcomes: PushOutcome[];
    try {
      outcomes = await this.pushData(pending.map(OfflineServiceBase.unsanitizeFromPouchDB));
    } catch (error) {
      // Push is best-effort inside sync: records stay pending and retry next cycle.
      console.error(`${this.constructor.name}: pushData failed; records stay pending`, error);
      return [];
    }
    let settledCount = 0;
    const settledOutcomes: PushOutcome[] = [];
    for (const { localId, serverId } of outcomes ?? []) {
      const pushed = pushedById.get(localId);
      const current = await this.disk.retrieve(this.#docKey(localId), true);
      if (!pushed || !current) {
        continue;
      }
      // The doc changed while the push was in flight (newer edit or a tombstone
      // written mid-push): what the server acknowledged is not what is on disk
      // now. Leave it pending so the next cycle pushes the newer state.
      if (current['_rev'] !== pushed['_rev']) {
        continue;
      }
      // Nothing came back to file the record under. Settling on it would drop the
      // local record and write it under a key naming nothing, losing it. Leave it
      // pending so the next cycle asks again.
      if (!serverId) {
        continue;
      }
      if (current['isDeleted']) {
        // The acknowledged push WAS the tombstone: drop it locally.
        await this.disk.remove(this.#docKey(localId));
        settledCount++;
        settledOutcomes.push({ localId, serverId });
        continue;
      }
      const { pendingPushToDynamics, _id, _rev, ...rest } = current;
      const settled = { ...rest, [this.idAttribute]: serverId };
      if (serverId !== localId) {
        // GUID swap: the doc key embeds the id, so the record moves to a new key.
        await this.disk.remove(this.#docKey(localId));
        await this.disk.updateOrInsert(this.#docKey(serverId), () => settled);
      } else {
        await this.disk.updateOrInsert(this.#docKey(localId), () => settled);
      }
      settledCount++;
      settledOutcomes.push({ localId, serverId });
    }
    this.#publishPendingCount(pending.length - settledCount);
    this.notifyLocalChange();
    return settledOutcomes;
  }

  async #findPendingDocs() {
    const docs = await this.disk.find<TRaw>({
      selector: {
        '_id': {
          $gte: this.dbKeyPrefix + '_',
          $lte: this.dbKeyPrefix + '_' + PREFIX_SEARCH_ENDKEY_UNICODE,
        },
      },
    }) ?? [];
    return docs.filter(doc => doc['pendingPushToDynamics'] === true);
  }

  async #refreshPendingCount() {
    if (!this.offlineDataCountKey) {
      return;
    }
    const pending = await this.#findPendingDocs();
    this.#publishPendingCount(pending.length);
  }

  #publishPendingCount(count: number) {
    if (this.offlineDataCountKey) {
      this.disk.setOfflineDataCount(this.offlineDataCountKey, Math.max(0, count));
    }
  }

  #docKey(id: string) {
    return `${this.dbKeyPrefix}_${id}`;
  }

  #generateOfflineId() {
    // Same `<prefix>_<rand36>_<timestamp>` shape as ActivityService's offline-id
    // generators, so positional parsers of the format hold across entities.
    return `${this.offlineIdPrefix}_${Math.random().toString(36).substring(2, 8)}_${new Date().getTime()}`;
  }
}
